import ExpiresAfter, {type ExpiresAfterName} from '../../../domain/ExpiresAfter';
import {CRYPTO_FORMAT_V1, type CryptoFormatVersion} from '../../crypto/chunks';
import {expiryDeadline} from '../../../home/expiryDeadline';
import type {KeyMode} from '../uploadContract';

/**
 * An unfinished hosted upload, as the browser keeps it so a later page can continue it
 * (kitecorp/transferify-ui#3204), and the rules for which one a page offers.
 *
 * WHAT IS KEPT, AND WHERE. The record lives in this origin's IndexedDB (`indexedDbUploadSessionStore.ts`),
 * in the sender's own browser profile, and holds what continuing needs that this browser never sends: the
 * transfer's raw AES key (as the browser's own `CryptoKey`; in the default mode the api holds only a copy
 * wrapped by its KMS key, and for Zero-Knowledge no copy at all), the secret seed the per-file IV prefixes
 * are derived from (`crypto/ivDerivation.ts`) and, for a Zero-Knowledge transfer, the finished share
 * fragment. Neither the raw key nor the seed ever leaves this browser, so an upload can be continued only in
 * the browser that started it, never on another device. The record is deleted when the upload finishes, is
 * discarded or can no longer be continued.
 *
 * The record also carries what the success screen and the recipient delivery need afterwards, because the
 * page that started the upload, and everything it held in memory, is gone by then.
 */

/**
 * Bumped whenever the record's shape or the IV derivation changes. A page never continues a session written
 * under another version: it could not read it right, and a mis-derived IV would corrupt the file it extends.
 *
 * A bump ends uploads, in both directions: {@link triageSessions} files a record of any other version as expired,
 * and `keptUploads.endPastSaving` then voids its transfer on the api and deletes the record. So after a bump, a tab
 * still on the older build voids the uploads the newer build kept, and the newer build voids every upload kept
 * before it. An added field that older records lack and older builds can ignore is therefore no reason to bump: the
 * cipher format ({@link UploadSessionRecord.cipherFormat}) came without one, because a bump would have voided every
 * upload kept before it, which are exactly the ones it was added to finish.
 */
export const UPLOAD_SESSION_VERSION = 1;

/**
 * The longest a session is offered, whatever the transfer's own expiry: past it, the parts of the files that
 * were mid-upload are long gone from storage (the bucket reaps an unfinished upload days after it began).
 */
export const MAX_SESSION_AGE_MS = 7 * 24 * 60 * 60_000;

/** One selected file as a later page must find it again, and as the upload declared it to the api. */
export interface SessionFile {
	name: string;
	size: number;
	/** The browser's `File.lastModified`, or null when it had none; part of what matches a file picked again. */
	lastModified: number | null;
	type: string;
	/** The folder path the file was picked or dropped with, including its name; absent for a flat file. */
	relativePath?: string;
	fileId: string;
	/** Plaintext bytes per cipher chunk, as declared on its initiate. */
	chunkSize: number;
}

/** Who the transfer goes to, so a continued upload can still notify them. */
export interface SessionDelivery {
	transferType: 'link' | 'recipients';
	toEmails: string[];
	groupIds: string[];
	message: string;
}

/** What the success card shows once the continued upload finishes. */
export interface SessionDisplay {
	/** By NAME: the enum's numbers are an app-internal identity a later build may renumber. */
	expireAfter: ExpiresAfterName;
	passwordProtected: boolean;
	maxDownloads?: number;
	initialRecipientEmails?: string[];
}

/** The escrowed key exactly as every file's initiate sent it, so a repeated initiate is recognised as one. */
export interface SessionEscrow {
	wrappedKey: string;
	kmsKeyId: string;
}

export interface UploadSessionRecord {
	version: number;
	id: string;
	/** The account that started the upload: only it is offered the session. */
	ownerUid: string;
	createdAt: number;
	/** The ENCODED transfer id the upload routes take. */
	transferId: string;
	keyMode: KeyMode;
	key: CryptoKey;
	ivSeed: Uint8Array<ArrayBuffer>;
	/** Present unless the transfer is Zero-Knowledge. */
	escrow?: SessionEscrow;
	/** Zero-Knowledge only: the `#k=`/`#p=` fragment the share link ends in, built before any byte moved. */
	shareFragment?: string;
	files: SessionFile[];
	delivery: SessionDelivery;
	display: SessionDisplay;
	/**
	 * The cipher format the upload writes every file in (kitecorp/transferify-ui#3349), which a continued upload keeps
	 * to, so the parts it sends join the parts already stored. Absent from a record kept before the field existed,
	 * when every writer wrote format 1 ({@link sessionCipherFormat}).
	 *
	 * What keeps one file's stream from mixing formats when a page continues in another one is the chunk-tag check
	 * (`sealedChunks.ts`): a chunk sealed in another format seals to another tag, so a continuation that seals again a
	 * chunk a stored part carries finds the change and starts that file again from its first part. This build always
	 * seals such a chunk first (`partResumePoint` starts one chunk early at a chunk boundary). A build from before
	 * this field ignores it, continues in format 1, and starts at the boundary chunk itself, so in one case it can
	 * still append format 1 chunks to a format 2 file: its continuation begins exactly on a chunk boundary, and the
	 * tag of that chunk was never kept because the page went between sealing it and sending its part. That takes a
	 * tab still on such a build continuing this build's session, and it ends with those tabs.
	 *
	 * A future format that older builds must not continue cannot simply bump {@link UPLOAD_SESSION_VERSION}: a bump
	 * voids kept uploads on both sides (see there). Plan that change with it, for example by first teaching
	 * {@link triageSessions} to leave a record of a higher version alone.
	 */
	cipherFormat?: CryptoFormatVersion;
}

/** How far the upload got, saved as it went. */
export interface UploadSessionProgress {
	/** Positions of the files storage holds whole. */
	completed: number[];
	/** Plaintext bytes storage had acknowledged when last saved. */
	uploadedBytes: number;
	updatedAt: number;
	/** The upload stopped because the connection did not come back in time, rather than by a reload. */
	interrupted: boolean;
}

/** A session as a page reads it back: the record, and its progress when any was saved. */
export interface StoredUploadSession {
	record: UploadSessionRecord;
	progress: UploadSessionProgress | null;
}

/** What the "Continue your upload" card says about a session. */
export interface SessionSummary {
	fileCount: number;
	totalBytes: number;
	uploadedBytes: number;
}

/** The cipher format a continued upload writes: the one the session recorded, or format 1 for one kept before then. */
export function sessionCipherFormat(record: Pick<UploadSessionRecord, 'cipherFormat'>): CryptoFormatVersion {
	return record.cipherFormat ?? CRYPTO_FORMAT_V1;
}

export function sessionSummary({record, progress}: StoredUploadSession): SessionSummary {
	const totalBytes = record.files.reduce((sum, file) => sum + file.size, 0);
	return {
		fileCount: record.files.length,
		totalBytes,
		uploadedBytes: Math.min(totalBytes, progress?.uploadedBytes ?? 0),
	};
}

/** The retention the session was created with, as the app's enum, or null for a name this build does not know. */
export function sessionExpireAfter(record: UploadSessionRecord): ExpiresAfter | null {
	const retention = ExpiresAfter[record.display.expireAfter];
	return typeof retention === 'number' ? retention : null;
}

/**
 * Until when a session may be continued: its transfer's link expiry (counted from the upload's start, as the
 * api counts it), and never past {@link MAX_SESSION_AGE_MS}.
 */
export function sessionDeadline(record: UploadSessionRecord): number {
	const retention = sessionExpireAfter(record);
	const linkExpiry = retention === null ? null : expiryDeadline(retention, record.createdAt);
	return Math.min(record.createdAt + MAX_SESSION_AGE_MS, linkExpiry ?? Number.POSITIVE_INFINITY);
}

/** Which sessions a page offers to continue, and which are past saving. */
export interface SessionTriage {
	/** The one session the "Continue your upload" card offers, or null. */
	offer: StoredUploadSession | null;
	/**
	 * Sessions that can no longer be continued: past their deadline, or written under another version. Never one
	 * a page is uploading right now: a tab still on the previous build, or an upload running past a short link's
	 * deadline, is left to end as it ends.
	 */
	expired: StoredUploadSession[];
}

/**
 * Picks the session to offer the signed-in sender: their own, still within its deadline, written under this
 * version, and not being uploaded right now by another tab or by this page's own running upload
 * (`liveSessionIds`, which the Web Locks the uploading pages hold answer). The newest wins; the others wait
 * for their turn. Another account's sessions are neither offered nor expired here, unless they are past
 * saving: those go whoever owns them, because only the browser holds them and nothing can continue them.
 */
export function triageSessions(sessions: readonly StoredUploadSession[], {ownerUid, now, liveSessionIds}: {
	ownerUid: string | null;
	now: number;
	liveSessionIds: ReadonlySet<string>;
}): SessionTriage {
	const expired = sessions.filter(session => !liveSessionIds.has(session.record.id)
		&& (session.record.version !== UPLOAD_SESSION_VERSION || now >= sessionDeadline(session.record)));
	const offerable = sessions
		.filter(session => !expired.includes(session) && ownerUid !== null && session.record.ownerUid === ownerUid
			&& !liveSessionIds.has(session.record.id))
		.sort((a, b) => b.record.createdAt - a.record.createdAt);
	return {offer: offerable[0] ?? null, expired};
}

/** The Web Lock a page holds while it uploads a session, so no other page offers to continue it. */
export function sessionLockName(sessionId: string): string {
	return `transferify:upload-session:${sessionId}`;
}

/** The session ids whose lock some page holds right now, read from a Web Locks snapshot. */
export function liveSessionIdsOf(heldLockNames: readonly (string | undefined)[]): Set<string> {
	const prefix = sessionLockName('');
	return new Set(heldLockNames
		.filter((name): name is string => name !== undefined && name.startsWith(prefix))
		.map(name => name.slice(prefix.length)));
}
